import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapTrifold, SquaresFour, ArrowsClockwise, MapPin, Buildings, Plus } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { eur, eurCompact } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/common";
import { placeImage } from "@/lib/placeImage";
import PlacesMap from "@/components/PlacesMap";
import PlaceCreateDialog from "@/components/PlaceCreateDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function PlaceCard({ p, onOpen, onView }) {
  const { t } = useTranslation();
  return (
    <div data-testid={`place-card-${p.id}`}
      className="group bg-white border border-slate-200 rounded-sm overflow-hidden shadow-[0_8px_30px_rgba(11,37,69,0.05)] hover:-translate-y-[3px] hover:shadow-[0_12px_40px_rgba(11,37,69,0.1)] transition-transform">
      <div className="relative h-44 overflow-hidden bg-slate-100 cursor-pointer" onClick={onOpen}>
        <img src={placeImage(p)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 bg-[#0B2545]/90 text-white text-xs px-2 py-1 rounded-sm">
          {p.property_count} {t("places.properties", "Properties")}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-serif text-lg text-slate-900">{p.name}</h3>
        <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5"><MapPin size={14} /> {[p.region, p.country].filter(Boolean).join(", ")}</div>
        <div className="font-serif text-xl text-[#0B2545] mt-3">{eur(p.portfolio_value)}</div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className="text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">{t("places.available", "Available")} {p.status_counts?.Available || 0}</span>
          <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">{t("places.reserved", "Reserved")} {p.status_counts?.Reserved || 0}</span>
          <span className="text-xs px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700">{t("places.underNegotiation", "Under negotiation")} {p.status_counts?.["Under negotiation"] || 0}</span>
          <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">{t("places.sold", "Sold")} {p.status_counts?.Sold || 0}</span>
        </div>
        <div className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
          {t("places.demand", "Buyer / investor demand")}: <b className="text-slate-700">{p.demand_buyers}</b> / <b className="text-slate-700">{p.demand_investors}</b>
        </div>
        <div className="flex gap-2 mt-4">
          <Button size="sm" onClick={onView} data-testid={`view-properties-${p.id}`} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white flex-1">{t("places.viewProperties", "View properties")}</Button>
          <Button size="sm" variant="outline" onClick={onOpen} data-testid={`open-profile-${p.id}`} className="rounded-sm flex-1">{t("places.openProfile", "Open Place Profile")}</Button>
        </div>
      </div>
    </div>
  );
}

export default function Places() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [view, setView] = useState("grid");
  const [showCreate, setShowCreate] = useState(false);
  const [country, setCountry] = useState("");
  const [minProps, setMinProps] = useState("");
  const [demand, setDemand] = useState("");
  const [ptype, setPtype] = useState("");
  const [status, setStatus] = useState("");

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta")).data });
  const { data: allPlaces = [] } = useQuery({ queryKey: ["places", "all"], queryFn: async () => (await api.get("/places")).data });

  const params = {};
  if (country) params.country = country;
  if (minProps) params.min_properties = minProps;
  if (demand) params.demand = demand;
  if (ptype) params.property_type = ptype;
  if (status) params.status = status;

  const { data: places = [], isLoading } = useQuery({
    queryKey: ["places", params],
    queryFn: async () => (await api.get("/places", { params })).data,
  });

  const generate = useMutation({
    mutationFn: async () => (await api.post("/places/generate")).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["places"] }); toast.success(t("places.generated", "Places updated from properties")); },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });

  const countries = [...new Set(allPlaces.map((p) => p.country).filter(Boolean))].sort();

  return (
    <div>
      <PageHeader
        title={t("places.title", "Places")}
        subtitle={t("places.subtitle", "Location intelligence for your portfolio")}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCreate(true)} data-testid="new-place-btn" className="rounded-sm">
              <Plus size={16} className="mr-1.5" /> {t("places.newPlace", "New Place")}
            </Button>
            <Button onClick={() => generate.mutate()} data-testid="generate-places-btn" disabled={generate.isPending}
              className="rounded-sm bg-[#C5A059] hover:bg-[#b08d4a] text-white">
              <ArrowsClockwise size={16} className={`mr-1.5 ${generate.isPending ? "animate-spin" : ""}`} /> {t("places.generate", "Generate from properties")}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col lg:flex-row flex-wrap gap-3 mb-6">
        <select data-testid="filter-country" value={country} onChange={(e) => setCountry(e.target.value)}
          className="h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm min-w-[10rem]">
          <option value="">{t("places.allCountries", "All countries")}</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filter-ptype" value={ptype} onChange={(e) => setPtype(e.target.value)}
          className="h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm min-w-[10rem]">
          <option value="">{t("places.allTypes", "All types")}</option>
          {meta?.property_categories?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm min-w-[9rem]">
          <option value="">{t("places.status", "Status")}</option>
          {meta?.property_statuses?.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-demand" value={demand} onChange={(e) => setDemand(e.target.value)}
          className="h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm min-w-[9rem]">
          <option value="">{t("places.anyDemand", "Any demand")}</option>
          <option value="buyers">{t("places.buyers", "Buyers")}</option>
          <option value="investors">{t("places.investors", "Investors")}</option>
        </select>
        <Input data-testid="filter-min-properties" type="number" min="0" value={minProps} onChange={(e) => setMinProps(e.target.value)}
          placeholder={t("places.minProperties", "Min. properties")} className="h-10 rounded-sm bg-white w-40" />
        <div className="flex border border-slate-200 rounded-sm bg-white ml-auto">
          {[["grid", SquaresFour], ["map", MapTrifold]].map(([v, Icon]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`places-view-${v}`}
              className={`px-3 py-2 transition-colors ${view === v ? "bg-[#0B2545] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
      ) : places.length === 0 ? (
        <EmptyState icon={Buildings} title={t("places.noPlaces", "No places yet")} subtitle={t("places.noPlacesHint", "Generate places from your properties to get started.")} />
      ) : view === "map" ? (
        <PlacesMap places={places} onOpenProfile={(p) => navigate(`/places/${p.id}`)} onViewProperties={(p) => navigate(`/places/${p.id}`)} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {places.map((p) => (
            <PlaceCard key={p.id} p={p} onOpen={() => navigate(`/places/${p.id}`)} onView={() => navigate(`/places/${p.id}`)} />
          ))}
        </div>
      )}

      <PlaceCreateDialog open={showCreate} onOpenChange={setShowCreate}
        onCreated={(p) => { qc.invalidateQueries({ queryKey: ["places"] }); navigate(`/places/${p.id}`); }} />
    </div>
  );
}
